import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as React from "react";
import { X as CloseIcon } from "../icons.phosphor.js";
import { releaseBodyScrollLockIfNoModalOpen } from "../internal/body-scroll-lock.js";
import { cx } from "../internal/cx.js";

/**
 * Sheet — a panel that slides in from an edge of the viewport.
 *
 * Different from Dialog (which is a centered modal that becomes a bottom
 * sheet on mobile). Sheet is an *intentional* side surface used for
 * filters, details views, navigation drawers, etc. It keeps the same
 * side-in behavior at every viewport size — you pick the side.
 *
 * Built on Radix Dialog so it gets the same focus trap, scroll lock, ESC
 * dismissal, and aria wiring as Dialog.
 */

export type SheetSide = "left" | "right" | "top" | "bottom";
export type SheetSize = "sm" | "md" | "lg" | "xl";

export function Sheet({
  onOpenChange,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root>) {
  return (
    <DialogPrimitive.Root
      {...props}
      onOpenChange={(open) => {
        onOpenChange?.(open);
        if (!open) {
          requestAnimationFrame(() => releaseBodyScrollLockIfNoModalOpen());
        }
      }}
    />
  );
}

export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetPortal = DialogPrimitive.Portal;
export const SheetClose = DialogPrimitive.Close;

export const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(function SheetOverlay({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cx("cw-v2-sheet__overlay", className)}
      {...props}
    />
  );
});
SheetOverlay.displayName = "SheetOverlay";

export interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /** Which edge the sheet slides in from. Default 'right'. */
  side?: SheetSide;
  /**
   * Panel size along its main axis:
   *   - left / right: controls the panel width.
   *   - top  / bottom: controls the panel height. (The inner content column
   *                    is always pinned to the design system's wide content
   *                    token, --cw-v2-content-wide / 1200px, regardless of
   *                    size — top/bottom sheets always feel like a wide
   *                    edge-anchored surface with the same reading column
   *                    as the rest of the app.)
   *
   * On viewports below 640px the sheet adapts: left/right cap at
   * min(95vw, 28rem); top/bottom keep the inner content cap and gain a
   * mobile gutter so the column never touches the screen edge.
   * Default 'md'.
   */
  size?: SheetSize;
  /** Hide the default close (X) button in the top-right of the sheet. */
  hideCloseButton?: boolean;
  /** Accessible label for the close button. */
  closeLabel?: string;
}

export const SheetContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(function SheetContent(
  {
    children,
    className,
    closeLabel = "Close panel",
    hideCloseButton = false,
    onAnimationEnd,
    onCloseAutoFocus,
    side = "right",
    size = "md",
    ...props
  },
  ref,
) {
  const scheduleScrollLockRelease = React.useCallback(() => {
    requestAnimationFrame(() => {
      releaseBodyScrollLockIfNoModalOpen();
    });
  }, []);

  React.useEffect(() => () => scheduleScrollLockRelease(), [scheduleScrollLockRelease]);

  return (
    <DialogPrimitive.Portal>
      <SheetOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cx(
          "cw-v2-theme",
          "cw-v2-sheet__content",
          `cw-v2-sheet__content--${side}`,
          `cw-v2-sheet__content--${size}`,
          className,
        )}
        onAnimationEnd={(event) => {
          onAnimationEnd?.(event);
          if (event.currentTarget !== event.target) return;
          if (event.currentTarget.getAttribute("data-state") !== "closed") return;
          scheduleScrollLockRelease();
        }}
        onCloseAutoFocus={(event) => {
          onCloseAutoFocus?.(event);
          scheduleScrollLockRelease();
        }}
        {...props}
      >
        {hideCloseButton ? null : (
          <DialogPrimitive.Close aria-label={closeLabel} className="cw-v2-sheet__close">
            <CloseIcon className="cw-v2-sheet__close-icon" weight="bold" aria-hidden />
          </DialogPrimitive.Close>
        )}
        <div className="cw-v2-sheet__inner">{children}</div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
});
SheetContent.displayName = "SheetContent";

/* ─── Layout subcomponents ─── */

export const SheetHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function SheetHeader({ className, ...props }, ref) {
    return <div ref={ref} className={cx("cw-v2-sheet__header", className)} {...props} />;
  },
);
SheetHeader.displayName = "SheetHeader";

export const SheetTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(function SheetTitle({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Title ref={ref} className={cx("cw-v2-sheet__title", className)} {...props} />
  );
});
SheetTitle.displayName = "SheetTitle";

export const SheetDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(function SheetDescription({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cx("cw-v2-sheet__description", className)}
      {...props}
    />
  );
});
SheetDescription.displayName = "SheetDescription";

export const SheetBody = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function SheetBody({ className, ...props }, ref) {
    return <div ref={ref} className={cx("cw-v2-sheet__body", className)} {...props} />;
  },
);
SheetBody.displayName = "SheetBody";

export const SheetFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function SheetFooter({ className, ...props }, ref) {
    return <div ref={ref} className={cx("cw-v2-sheet__footer", className)} {...props} />;
  },
);
SheetFooter.displayName = "SheetFooter";
